import React from 'react';
import { useComposedRefs } from '@radix-ui/react-compose-refs';
import { FocusScope } from '@radix-ui/react-focus-scope';
import { uniqueId } from 'lodash';

import { bemBlock } from '../../modules/bem';
import { ActionsMenu } from '../actions-menu/ActionsMenu';
import { Icon } from '../icon/Icon';
import { LayoutFill } from '../layout-fill/LayoutFill';
import { LayoutRow } from '../layout-row/LayoutRow';
import { Popover } from '../popover/Popover';
import { ScrollArea } from '../scroll-area/ScrollArea';
import { Text } from '../text/Text';

import './ComboBox.less';

const block = bemBlock('combo-box');

const SELECT_EVENT = `n-combobox-item-select`;
const MENU_ITEM_ID_PREFIX = 'n-combobox-menu-item-';

type MenuContextType = {
  inputElement: HTMLInputElement | null;
  highlightedItemId: string | undefined;
  highlightItem: (itemId: string | undefined) => void;
  registerInput: (inputRef: HTMLInputElement) => void;
};

const MenuContext = React.createContext<MenuContextType | null>(null);

const useComboBoxContext = () => {
  const context = React.useContext(MenuContext);

  if (!context) {
    throw new Error('Attempting to use ComboBox.Item outside ComboBox.Root');
  }

  return context;
};

function findMenuItems(element: Element): Element[] {
  return Array.from(element.querySelectorAll('[role="menuitem"]:not([aria-disabled="true"])'));
}

function highlightedItemSelector(id: string) {
  return `[id="${id}"]:not([aria-disabled="true"])`;
}

const Menu = React.forwardRef<React.ElementRef<'div'>, React.ComponentPropsWithoutRef<'div'>>(
  function Menu({ children, className, onKeyDown, ...rest }, ref) {
    const [highlightedItemId, setHighlightedItemId] = React.useState<string>();

    const menuRef = React.useRef<HTMLDivElement | null>();
    const searchInputRef = React.useRef<HTMLInputElement | null>(null);

    const composeRefs = useComposedRefs(menuRef, ref);

    const registerInput = React.useCallback((ref: HTMLInputElement) => {
      searchInputRef.current = ref;
    }, []);

    const handleInputFocus = React.useCallback(() => {
      if (searchInputRef.current !== document.activeElement) {
        searchInputRef.current?.focus({ preventScroll: true });
      }
    }, []);

    const handleKeyDown: React.KeyboardEventHandler<HTMLDivElement> = React.useCallback(
      (event) => {
        const targetElement = event.target as HTMLElement;

        if (!targetElement.closest(`[data-combo-box-content]`)) {
          return;
        }

        onKeyDown?.(event);

        const registeredItemIds = menuRef.current
          ? findMenuItems(menuRef.current).map((item) => item.id)
          : [];

        const totalItems = registeredItemIds.length;
        const selectedItemIndex = registeredItemIds.findIndex((id) => id === highlightedItemId);

        switch (event.key) {
          case 'ArrowUp': {
            const next = selectedItemIndex - 1 < 0 ? totalItems - 1 : selectedItemIndex - 1;
            setHighlightedItemId?.(registeredItemIds[next]);
            break;
          }

          case 'ArrowDown': {
            const next = selectedItemIndex + 1 === totalItems ? 0 : selectedItemIndex + 1;
            setHighlightedItemId?.(registeredItemIds[next]);
            break;
          }

          case 'Home': {
            setHighlightedItemId?.(registeredItemIds[0]);
            break;
          }

          case 'End': {
            setHighlightedItemId?.(registeredItemIds[totalItems - 1]);
            break;
          }

          case 'Enter': {
            if (highlightedItemId !== undefined) {
              const item = menuRef.current?.querySelector(
                highlightedItemSelector(highlightedItemId),
              );

              if (item) {
                item.dispatchEvent(new Event(SELECT_EVENT));
              }
            }

            break;
          }

          default:
            break;
        }
      },
      [onKeyDown, highlightedItemId],
    );

    const value = React.useMemo(
      () => ({
        registerInput,
        highlightedItemId,
        inputElement: searchInputRef.current,
        highlightItem: setHighlightedItemId,
      }),
      [highlightedItemId, setHighlightedItemId, registerInput],
    );

    return (
      <MenuContext.Provider value={value}>
        <div
          role="combobox"
          ref={composeRefs}
          onKeyDown={handleKeyDown}
          onPointerUp={handleInputFocus}
          onMouseEnter={handleInputFocus}
          onKeyDownCapture={handleInputFocus}
          aria-activedescendant={highlightedItemId}
          className={block({ extra: className })}
          {...rest}
        >
          {children}
        </div>
      </MenuContext.Provider>
    );
  },
);

type MenuItemGroupProps = React.PropsWithChildren<{
  heading: React.ReactNode;
  className?: string;
}>;

const ItemGroup = React.forwardRef<
  React.ElementRef<'div'>,
  React.ComponentPropsWithoutRef<'div'> & MenuItemGroupProps
>(function ItemGroup({ children, heading, className, ...rest }, ref) {
  let headingElement = heading;

  if (typeof headingElement === 'string') {
    headingElement = (
      <Text size="xs" className={block('group-heading-text')}>
        {heading}
      </Text>
    );
  }

  return (
    <div ref={ref} className={block({ element: 'item-group', extra: className })} {...rest}>
      <div className={block('group-heading')}>{headingElement}</div>
      <ScrollArea>
        <ScrollArea.Viewport
          style={{
            maxHeight: 300,
            maxWidth: 'var(--radix-popper-available-width)',
          }}
        >
          {children}
        </ScrollArea.Viewport>
        <ScrollArea.Scrollbar orientation="vertical" />
        <ScrollArea.Scrollbar orientation="horizontal" />
        <ScrollArea.Corner />
      </ScrollArea>
    </div>
  );
});

const SearchInput = React.forwardRef<
  React.ElementRef<'input'>,
  React.ComponentPropsWithoutRef<'input'> & {
    prefixElement?: React.ReactNode;
    suffixElement?: React.ReactNode;
  }
>(function SearchInput({ className, prefixElement, suffixElement, ...props }, ref) {
  const { registerInput } = useComboBoxContext();

  const composeRefs = useComposedRefs(ref, registerInput);

  const handleKeyDown: React.KeyboardEventHandler<HTMLInputElement> = React.useCallback((e) => {
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault();
    }

    if (e.key === 'ArrowLeft' && e.currentTarget.selectionStart !== 0) {
      e.stopPropagation();
    }
  }, []);

  return (
    <>
      <div className={block('header')}>
        <FocusScope asChild loop trapped>
          <div className={block('search-input-wrapper')}>
            {prefixElement ?? <Icon glyph="search" className={block('search-input-icon')} />}
            <input
              type="text"
              ref={composeRefs}
              onKeyDown={handleKeyDown}
              className={block({ element: 'search-input-input', extra: className })}
              {...props}
            />
            {suffixElement}
          </div>
        </FocusScope>
      </div>
      <Separator />
    </>
  );
});

type MenuItemProps = React.PropsWithChildren<{
  disabled?: boolean;
  leadingIcon?: React.ReactNode;
  trailingElement?: React.ReactNode;
  onSelect?: (event: React.MouseEvent<HTMLDivElement>) => void;
}>;

const Item = React.forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<'div'> & MenuItemProps
>(function MenuItem(
  { leadingIcon, trailingElement, children, onMouseLeave, onSelect, className, disabled, ...rest },
  ref,
) {
  const itemRef = React.useRef<HTMLDivElement | null>();
  const [id] = React.useState<string>(() => uniqueId(MENU_ITEM_ID_PREFIX));

  const { highlightedItemId, highlightItem } = useComboBoxContext();

  const isSelected = highlightedItemId === id;

  React.useLayoutEffect(() => {
    if (!itemRef.current || !isSelected) {
      return;
    }

    if (itemRef.current.matches(':hover')) {
      return;
    }

    itemRef.current.scrollIntoView({
      block: 'center',
    });
  }, [isSelected]);

  React.useEffect(() => {
    const element = itemRef.current;

    if (!element || !onSelect || disabled) {
      return;
    }

    const handleSelect = (event: Event) => {
      onSelect(event as unknown as React.MouseEvent<HTMLDivElement>);
    };

    element.addEventListener(SELECT_EVENT, handleSelect);

    return () => element.removeEventListener(SELECT_EVENT, handleSelect);
  }, [onSelect, disabled]);

  const handleMouseMove: React.MouseEventHandler<HTMLDivElement> = React.useCallback(
    (event) => {
      if (event.defaultPrevented || !itemRef.current || disabled) {
        return;
      }

      highlightItem(itemRef.current.id);
    },
    [highlightItem, disabled],
  );

  const composeRefs = useComposedRefs(itemRef, ref);

  return (
    <LayoutRow
      elementRef={composeRefs}
      role="menuitem"
      onMouseMove={handleMouseMove}
      onClick={onSelect}
      className={block({ element: 'item', extra: className })}
      span="auto"
      alignItems="center"
      spacedChildren="sm"
      tabIndex={isSelected ? 0 : -1}
      aria-disabled={disabled === true ? 'true' : undefined}
      data-disabled={disabled === true ? '' : undefined}
      data-highlighted={isSelected === true ? '' : undefined}
      {...rest}
      id={id}
    >
      {leadingIcon}
      {children}
      {trailingElement && <LayoutFill />}
      {trailingElement}
    </LayoutRow>
  );
});

const Separator = React.forwardRef<React.ElementRef<'div'>, React.ComponentPropsWithoutRef<'div'>>(
  function Separator({ className, ...props }, ref) {
    return (
      <div ref={ref} className={block({ element: 'separator', extra: className })} {...props} />
    );
  },
);

const ActionsMenuContent = React.forwardRef<
  React.ElementRef<typeof ActionsMenu.SubContent>,
  React.ComponentPropsWithoutRef<typeof ActionsMenu.SubContent> & {
    className?: string;
    children: React.ReactNode;
  }
>(function ActionsMenuContent({ children, className, ...props }, ref) {
  return (
    <ActionsMenu.SubContent
      data-combo-box-content
      scrollArea={React.Fragment}
      className={block({ extra: className })}
      {...props}
      ref={ref}
    >
      <Menu>{children}</Menu>
    </ActionsMenu.SubContent>
  );
});

const PopoverContent = React.forwardRef<
  React.ElementRef<typeof Popover.Content>,
  React.ComponentPropsWithoutRef<typeof Popover.Content>
>(function PopoverContent({ className, style, children, ...props }, ref) {
  return (
    <Popover.Content
      data-combo-box-content
      scrollArea={React.Fragment}
      className={block({ extra: className })}
      {...props}
      ref={ref}
    >
      <Menu>{children}</Menu>
    </Popover.Content>
  );
});

export const ComboBox = {
  Root: Popover.Root,
  Trigger: Popover.Trigger,
  Portal: Popover.Portal,
  PopoverContent,
  ActionsMenuContent,
  SearchInput,
  ItemGroup,
  Item,
  Separator,
};
